// components/Profile/P4UserProfile.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../api";
import styles from "../Home/HomePage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useAuth } from "../context/AuthContext";
const authenticatedFetch = (url, options = {}) => {
  const token =
    localStorage.getItem("auth_token") || localStorage.getItem("token");
  if (!token) {
    window.location.href = "/#/login";
    throw new Error("No auth token");
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
};

export default function P4UserProfile() {
  const { token, profile } = useAuth();
  localStorage.setItem("navbar_state", "user");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    whatsapp_number: "",
  });

  // accounts
  const [accounts, setAccounts] = useState([]);
  const [accLoading, setAccLoading] = useState(false);
  const [accError, setAccError] = useState("");
  const [verifyModal, setVerifyModal] = useState(false);
  const [verifyAccount, setVerifyAccount] = useState(null);
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [verifyErr, setVerifyErr] = useState("");
  const [verifyMsg, setVerifyMsg] = useState("");

  const [verifyForm, setVerifyForm] = useState({
    id_or_reg_no: "",
    identifier_type: "SA_ID",
    supporting_doc: null,
  });

  // add account modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [acctSubmitting, setAcctSubmitting] = useState(false);
  const [acctMsg, setAcctMsg] = useState("");
  const [acctErr, setAcctErr] = useState("");

  const [acctForm, setAcctForm] = useState({
    account_name: "",
    email: "",
    phone: "",
    id_or_reg_no: "",
    identifier_type: "SA_ID",
    supporting_doc: null,
    address_street: "",
    address_suburb: "",
    address_postal_code: "",
    address_province: "",
    address_country: "",
  });

  const [acceptedTcs, setAcceptedTcs] = useState(null);
  const [showTcsModal, setShowTcsModal] = useState(false);
  const [tcsSubmitting, setTcsSubmitting] = useState(false);
  const [tcsError, setTcsError] = useState("");

  const API = "https://simplix.co.za/api";
  //const API = "http://127.0.0.1:8000/api";

  const TCS_PDF_URL =
    "/legal/p4power-consumer-portal-eula-and-service-terms.pdf";

  const TCS_ACCEPTANCE_TEXT =
    'By clicking "I Accept", I agree to be bound by the P4Power Consumer Portal End User Licence Agreement and Service Terms, including the electronic communication and information-processing provisions described in those terms.';

  // ===== Validation helpers (added) ==========================================
  const REQUIRE_DOC = true; // make supporting doc mandatory for new accounts

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

  const normalizePhoneNumber = (value) => {
    let raw = String(value || "").trim();

    if (!raw) return "";

    const explicitlyInternational = raw.startsWith("+") || raw.startsWith("00");

    // Handle formats such as +27 (0)82 123 4567.
    if (explicitlyInternational) {
      raw = raw.replace(/\(0\)/g, "");
    }

    let digits = raw.replace(/\D/g, "");

    if (digits.startsWith("00")) {
      digits = digits.slice(2);
    }

    // Numbers entered in South African local format.
    if (digits.startsWith("0")) {
      digits = `27${digits.slice(1)}`;
    }

    return digits ? `+${digits}` : "";
  };

  const isValidInternationalPhone = (value) => {
    const normalized = normalizePhoneNumber(value);
    const digits = normalized.replace(/\D/g, "");

    // E.164 permits a maximum of 15 digits.
    return /^[1-9]\d{6,14}$/.test(digits);
  };
  // Luhn for SA ID
  const luhn = (digits) => {
    let sum = 0,
      dbl = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = digits.charCodeAt(i) - 48;
      if (dbl) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      dbl = !dbl;
    }
    return sum % 10 === 0;
  };
  const validYYMMDD = (yy, mm, dd) => {
    const y = Number(yy),
      m = Number(mm),
      d = Number(dd);
    if (m < 1 || m > 12 || d < 1 || d > 31) return false;
    const fullYear = 1900 + y; // simple check; we only need date existence
    const dt = new Date(fullYear, m - 1, d);
    return (
      dt.getFullYear() === fullYear &&
      dt.getMonth() === m - 1 &&
      dt.getDate() === d
    );
  };
  const isValidSouthAfricanID = (s) => {
    const v = (s || "").trim();
    if (!/^\d{13}$/.test(v)) return false;
    if (!validYYMMDD(v.slice(0, 2), v.slice(2, 4), v.slice(4, 6))) return false;
    return luhn(v);
  };

  // CIPC reg formats: 2014/123456/07, K2014/123456/07, CK14/123456/07, CC14/123456/07
  const isValidCompanyReg = (s) => {
    const v = (s || "").trim();
    const re1 = /^\d{4}\/\d{6}\/\d{2}$/;
    const re2 = /^K\d{4}\/\d{6}\/\d{2}$/i;
    const re3 = /^(CK|CC)\d{2}\/\d{6}\/\d{2}$/i;
    return re1.test(v) || re2.test(v) || re3.test(v);
  };

  const isValidPassportNumber = (s) =>
    /^[A-Z0-9]{6,15}$/.test((s || "").trim().toUpperCase());

  // ==========================================================================

  async function getAccountsAPI(email) {
    const r = await authenticatedFetch(`${API}/accounts`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!r.ok)
      throw new Error(await r.text().catch(() => "Failed to load accounts"));
    return r.json();
  }

  async function loadAccounts(userEmail) {
    if (!userEmail) {
      setAccounts([]);
      return;
    }
    setAccLoading(true);
    setAccError("");
    try {
      const j = await getAccountsAPI(userEmail);
      setAccounts(Array.isArray(j?.accounts) ? j.accounts : []);
    } catch (e) {
      setAccError(e.message || "Could not load accounts");
      setAccounts([]);
    } finally {
      setAccLoading(false);
    }
  }

  const hasPendingAccounts = accounts.some(
    (a) => String(a?.status || "").toLowerCase() !== "active",
  );
  function updateProfileIncompleteStatus(
    profileForm,
    termsAccepted = acceptedTcs,
  ) {
    const incomplete =
      !profileForm.email?.trim() ||
      !profileForm.mobile_number?.trim() ||
      !profileForm.whatsapp_number?.trim() ||
      termsAccepted !== true;

    localStorage.setItem("profile_incomplete", incomplete ? "true" : "false");

    localStorage.setItem(
      "tcs_accepted",
      termsAccepted === true ? "true" : "false",
    );

    window.dispatchEvent(new Event("profile-status-changed"));
  }

  useEffect(() => {
    const notice = sessionStorage.getItem("profile_notice");

    if (notice) {
      setMsg(notice);
      sessionStorage.removeItem("profile_notice");
    }

    (async () => {
      setLoading(true);
      setError("");

      try {
        const email = (
          localStorage.getItem("userEmail") ||
          JSON.parse(localStorage.getItem("profile") || "{}").email ||
          ""
        ).trim();

        if (!email) {
          setLoading(false);
          return;
        }

        const r = await authenticatedFetch(`${API}/profile`);

        if (!r.ok) throw new Error("Failed to load profile");

        const j = await r.json();
        const p = j.profile || {};
        const termsAccepted = p.acceptedTcs === true;

        const loadedForm = {
          first_name: p.firstName || "",
          last_name: p.lastName || "",
          email: p.email || email,
          mobile_number: p.mobile || "",
          whatsapp_number: p.whatsappNumber || "",
        };

        setForm(loadedForm);
        setAcceptedTcs(termsAccepted);
        setShowTcsModal(!termsAccepted);
        updateProfileIncompleteStatus(loadedForm, termsAccepted);
        await loadAccounts(p.email || email);
      } catch (e) {
        setError("Could not load profile");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function acceptTerms() {
    if (tcsSubmitting) return;

    const email = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();
    if (!email) {
      setTcsError("Could not identify the current user profile.");
      return;
    }

    setTcsSubmitting(true);
    setTcsError("");

    try {
      const r = await authenticatedFetch(`${API}/profile/accept-terms/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          tcs_accepted: true,
        }),
      });

      if (!r.ok) {
        throw new Error(
          await r.text().catch(() => "Could not save Terms acceptance"),
        );
      }

      setAcceptedTcs(true);
      setShowTcsModal(false);
      updateProfileIncompleteStatus(form, true);

      try {
        const storedProfile =
          JSON.parse(localStorage.getItem("profile") || "{}") || {};

        localStorage.setItem(
          "profile",
          JSON.stringify({
            ...storedProfile,
            acceptedTcs: true,
          }),
        );
      } catch {
        localStorage.setItem("profile", JSON.stringify({ acceptedTcs: true }));
      }

      setMsg("Terms & Conditions accepted.");
    } catch (e) {
      setTcsError(e.message || "Could not save Terms acceptance");
    } finally {
      setTcsSubmitting(false);
    }
  }

  const missingFields = [];

  if (!form.email?.trim()) {
    missingFields.push("Email Address");
  }

  if (!form.mobile_number?.trim()) {
    missingFields.push("Mobile Number");
  }

  if (!form.whatsapp_number?.trim()) {
    missingFields.push("WhatsApp Number");
  }
  async function save(e) {
    e.preventDefault();
    setError("");
    setMsg("");

    // ---- Client-side validation for profile (added)
    if (form.email && !isEmail(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (form.mobile_number && !isValidInternationalPhone(form.mobile_number)) {
      setError(
        "Enter a valid phone number including the country code, for example +27 xx xxx xxxx.",
      );
      return;
    }
    if (
      form.whatsapp_number &&
      !isValidInternationalPhone(form.whatsapp_number)
    ) {
      setError(
        "Enter a valid WhatsApp number including the country code, for example +27 xx xxx xxxx.",
      );
      return;
    }

    try {
      const currentEmail =(
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();
      const body = {
        current_email: currentEmail,
        email: (form.email || "").trim().toLowerCase(),
        first_name: form.first_name || "",
        last_name: form.last_name || "",
        mobile_number: normalizePhoneNumber(form.mobile_number),
        whatsapp_number: normalizePhoneNumber(form.whatsapp_number),
      };
      const r = await authenticatedFetch(`${API}/profile/update/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.status === 409) throw new Error("That email is already in use.");
      if (!r.ok)
        throw new Error(await r.text().catch(() => "Could not update profile"));
      updateProfileIncompleteStatus(form);
      setMsg("Profile updated.");
      const j = await r.json();
      if (j?.email) {
        localStorage.setItem("userEmail", j.email);
        await loadAccounts(j.email);
      }
    } catch (e2) {
      setError(e2.message || "Update failed");
    }
  }

  function resetAcctForm() {
    setAcctForm({
      account_name: "",
      email: "",
      phone: "",
      id_or_reg_no: "",
      identifier_type: "SA_ID",
      supporting_doc: null,
      address_street: "",
      address_suburb: "",
      address_postal_code: "",
      address_province: "",
      address_country: "",
    });
    setAcctErr("");
    setAcctMsg("");
  }
  function openAddModal() {
    resetAcctForm();
    setShowAddModal(true);
  }
  function closeAddModal() {
    setShowAddModal(false);
  }

  const Badge = ({ status }) => {
    const isActive = String(status || "").toLowerCase() === "active";
    const bg = isActive ? "var(--ok-ghost)" : "var(--warn-ghost)";
    const fg = isActive ? "var(--ok)" : "var(--warn)";
    return (
      <span
        className="badge"
        style={{ background: bg, color: fg, border: `1px solid ${fg}` }}
      >
        {isActive ? "Active" : "Pending"}
      </span>
    );
  };

  const ReadOnlyRow = ({ label, value }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "180px 1fr",
        gap: 8,
        alignItems: "center",
        margin: "6px 0",
      }}
    >
      <div style={{ opacity: 0.7 }}>{label}</div>
      <div>{value || <em style={{ opacity: 0.6 }}>—</em>}</div>
    </div>
  );

  if (loading)
    return (
      <div className="container">
        <p>Loading…</p>
      </div>
    );

  function getDisplayName(a) {
    const nm = (
      a?.companyName || `${a?.firstName || ""} ${a?.lastName || ""}`.trim()
    ).trim();
    return nm || "Account";
  }
  function getDisplayEmail(a) {
    return a?.companyEmail || a?.email || "";
  }
  function getDisplayRegId(a) {
    return a?.companyRegNo || a?.idNumber || "";
  }

  // --- NEW: navigate to the Wallet page with the account GUID ----------------
  function goToWallet(a) {
    const guid = (a?.id || "").trim();
    if (!guid) return;
    // Prefer route-param style; WalletPage also supports query string fallback
    navigate(`/accounts/${encodeURIComponent(guid)}/wallet`);
    // Fallback option if you only use query style:
    // navigate(`/wallet?accountId=${encodeURIComponent(guid)}`);
  }
  // ---------------------------------------------------------------------------
  async function verifyPendingAccountAPI(accountId, formData) {
    const r = await authenticatedFetch(
      `${API}/accounts/${accountId}/verify-pending/`,
      {
        method: "POST",
        body: formData,
      },
    );

    if (!r.ok) {
      throw new Error(await r.text().catch(() => "Could not verify account"));
    }

    return r.json();
  }

  function openVerifyModal(account) {
    setVerifyAccount(account);
    setVerifyForm({
      id_or_reg_no: "",
      identifier_type: "SA_ID",
      supporting_doc: null,
    });
    setVerifyErr("");
    setVerifyMsg("");
    setVerifyModal(true);
  }

  function closeVerifyModal() {
    setVerifyModal(false);
    setVerifyAccount(null);
  }
  const ALLOWED_SUPPORTING_DOC_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];

  const isValidSupportingDoc = (file) =>
    file && ALLOWED_SUPPORTING_DOC_TYPES.includes(file.type);
  async function submitPendingVerification(e) {
    e.preventDefault();
    setVerifyErr("");
    setVerifyMsg("");
    setVerifySubmitting(true);

    try {
      if (!verifyAccount?.id) {
        throw new Error("No account selected.");
      }

      if (!verifyForm.id_or_reg_no.trim()) {
        throw new Error(
          "Please enter an SA ID number, passport number, or company registration number.",
        );
      }

      const kind = verifyForm.identifier_type;

      if (kind === "SA_ID" && !isValidSouthAfricanID(verifyForm.id_or_reg_no)) {
        throw new Error(
          "Invalid South African ID number. It must be 13 digits with a valid date and checksum.",
        );
      }

      if (kind === "CIPC" && !isValidCompanyReg(verifyForm.id_or_reg_no)) {
        throw new Error(
          "Invalid Company Registration No. Use a format like 2014/123456/07, K2014/123456/07, CK14/123456/07.",
        );
      }

      if (
        kind === "PASSPORT" &&
        !isValidPassportNumber(verifyForm.id_or_reg_no)
      ) {
        throw new Error(
          "Invalid passport number. Use 6 to 15 letters and numbers, without spaces.",
        );
      }

      if (!verifyForm.supporting_doc) {
        throw new Error(
          "Please upload the ID or company registration document.",
        );
      }

      if (!isValidSupportingDoc(verifyForm.supporting_doc)) {
        throw new Error(
          "Supporting document must be a PDF, JPG, JPEG or PNG file.",
        );
      }

      const fd = new FormData();

      fd.append("identifier_type", kind);
      fd.append(
        "id_number",
        kind === "CIPC"
          ? ""
          : kind === "PASSPORT"
            ? verifyForm.id_or_reg_no.trim().toUpperCase()
            : verifyForm.id_or_reg_no.trim(),
      );
      fd.append(
        "company_reg_no",
        kind === "CIPC" ? verifyForm.id_or_reg_no.trim() : "",
      );

      fd.append("supporting_doc", verifyForm.supporting_doc);

      await verifyPendingAccountAPI(verifyAccount.id, fd);

      const userEmail = form.email || (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim()|| "";
      await loadAccounts(userEmail);

      setVerifyMsg("Account verified and activated.");
      setTimeout(() => {
        closeVerifyModal();
      }, 700);
    } catch (e2) {
      setVerifyErr(e2.message || "Verification failed.");
    } finally {
      setVerifySubmitting(false);
    }
  }
  return (
    <div className={styles.page}>
      {showTcsModal && (
        <div
          className={`${styles.modalBackdrop} ${styles.tcsModalBackdrop}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-tcs-title"
        >
          <div className={`${styles.authModal} ${styles.tcsScrollableModal}`}>
            <h2 id="profile-tcs-title">Terms &amp; Conditions</h2>

            <p>
              You may download the Terms &amp; Conditions below. Downloading the
              document is optional.
            </p>

            <a className={styles.tcsPdfLink} href={TCS_PDF_URL} download>
              Download Terms &amp; Conditions (PDF)
            </a>

            <div className={styles.tcsConfirmation}>
              <span>{TCS_ACCEPTANCE_TEXT}</span>
            </div>

            {tcsError && <div className={styles.authError}>{tcsError}</div>}

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={tcsSubmitting}
                onClick={acceptTerms}
              >
                {tcsSubmitting ? "Saving..." : "I Accept"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`${styles.section} ${styles.meterDetailSection}`}>
        <div className={styles.meterDetailHero}>
          <div>
            <p className={styles.sectionEyebrow}>Account</p>
            <h1 className={styles.h2}>Your profile</h1>
            <p className={styles.sectionIntro}>
              Manage your personal details and linked accounts.
            </p>
          </div>
        </div>

        <div className={styles.meterDetailPanel}>
          <div className={styles.meterDetailPanelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>Profile</p>
              <h2>Personal details</h2>
              {missingFields.length > 0 && (
                <div className={styles.profileMissingBanner}>
                  <strong>⚠ Action Required</strong>

                  <p>
                    Please complete the following information to ensure you
                    receive account notifications:
                  </p>

                  <ul>
                    {missingFields.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <form className={styles.authForm} onSubmit={save}>
            <div className={styles.authRow}>
              <input
                className={styles.authInput}
                placeholder="First name"
                value={form.first_name}
                onChange={(e) =>
                  setForm({ ...form, first_name: e.target.value })
                }
              />
              <input
                className={styles.authInput}
                placeholder="Last name"
                value={form.last_name}
                onChange={(e) =>
                  setForm({ ...form, last_name: e.target.value })
                }
              />
            </div>

            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 18,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 18,
                  zIndex: 2,
                }}
              >
                📧
              </span>

              <input
                className={styles.authInput}
                type="email"
                placeholder="Email Address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={{ paddingLeft: 52 }}
              />
            </div>

            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 18,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#64748b",
                  zIndex: 2,
                }}
              >
                <FontAwesomeIcon icon={faPhone} />
              </span>

              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className={styles.authInput}
                placeholder="Mobile Number"
                value={form.mobile_number}
                onChange={(e) =>
                  setForm({ ...form, mobile_number: e.target.value })
                }
                style={{ paddingLeft: 52 }}
              />
            </div>

            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 18,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#25D366",
                  zIndex: 2,
                }}
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </span>

              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className={styles.authInput}
                placeholder="WhatsApp Number"
                value={form.whatsapp_number}
                onChange={(e) =>
                  setForm({ ...form, whatsapp_number: e.target.value })
                }
                style={{ paddingLeft: 52 }}
              />
            </div>

            {error && <div className={styles.authError}>{error}</div>}
            {msg && <div className={styles.authSuccess}>{msg}</div>}

            <div className={styles.profileActions}>
              <button
                type="button"
                className={styles.inlineBtn}
                onClick={() => {
                  const e =
                    form.email || (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim() || "";
                  navigate(`/change-password?email=${encodeURIComponent(e)}`);
                }}
              >
                Change password
              </button>

              <button className={styles.primaryBtn} type="submit">
                Save changes
              </button>
            </div>
          </form>
        </div>

        <div className={styles.meterDetailPanel}>
          <div className={styles.meterDetailPanelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>Accounts</p>
              <h2>Linked accounts</h2>
              <p className={styles.meterPanelText}>
                While an account is <strong>Pending</strong>, you cannot use it
                for payments or meters.
              </p>
            </div>
          </div>

          {accLoading && (
            <div className={styles.meterLoadingBox}>Loading accounts…</div>
          )}
          {accError && <div className={styles.authError}>{accError}</div>}

          {!accLoading && !accError && accounts.length === 0 && (
            <div className={styles.meterPanelText}>No accounts linked yet.</div>
          )}

          {!accLoading && !accError && accounts.length > 0 && (
            <div className={styles.profileAccountGrid}>
              {accounts.map((a) => {
                const name = getDisplayName(a);
                const email = getDisplayEmail(a);
                const regId = getDisplayRegId(a);
                const active = String(a.status).toLowerCase() === "active";

                return (
                  <div
                    key={a.id}
                    className={styles.profileAccountCard}
                    style={{ opacity: active ? 1 : 0.72 }}
                  >
                    <div className={styles.profileAccountHeader}>
                      <h3>{name}</h3>
                      <span className={styles.badge}>
                        {active ? "Active" : "Pending"}
                      </span>
                    </div>

                    <div className={styles.profileAccountDetails}>
                      <div>
                        <span>Email</span>
                        <strong>{email || "—"}</strong>
                      </div>

                      <div>
                        <span>Phone</span>
                        <strong>{a.phone || "—"}</strong>
                      </div>

                      <div>
                        <span>ID / Reg</span>
                        <strong>{regId || "—"}</strong>
                      </div>

                      <div>
                        <span>Address</span>
                        <strong>
                          {[
                            a.address?.street,
                            a.address?.suburb,
                            a.address?.postal_code,
                            a.address?.province,
                            a.address?.country,
                          ]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </strong>
                      </div>
                    </div>

                    <div className={styles.profileAccountActions}>
                      {!active && !regId && (
                        <button
                          className={styles.primaryBtn}
                          onClick={() => openVerifyModal(a)}
                        >
                          Add ID / Activate
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {verifyModal && (
          <div
            className={styles.modalBackdrop}
            onClick={(e) => e.target === e.currentTarget && closeVerifyModal()}
          >
            <div className={styles.authModal}>
              <h2>Verify pending account</h2>

              <p className={styles.meterPanelText}>
                Add the missing SA ID, passport, or company registration
                document to activate this account.
              </p>

              <form
                onSubmit={submitPendingVerification}
                className={styles.authForm}
              >
                <select
                  className={styles.authInput}
                  value={verifyForm.identifier_type}
                  onChange={(e) =>
                    setVerifyForm((p) => ({
                      ...p,
                      identifier_type: e.target.value,
                    }))
                  }
                >
                  <option value="SA_ID">South African ID</option>
                  <option value="PASSPORT">Passport number</option>
                  <option value="CIPC">Company registration number</option>
                </select>
                <input
                  className={styles.authInput}
                  placeholder={
                    verifyForm.identifier_type === "SA_ID"
                      ? "South African ID number"
                      : verifyForm.identifier_type === "PASSPORT"
                        ? "Passport number"
                        : "Company Reg No (e.g. 2014/123456/07)"
                  }
                  value={verifyForm.id_or_reg_no}
                  onChange={(e) =>
                    setVerifyForm((p) => ({
                      ...p,
                      id_or_reg_no: e.target.value,
                    }))
                  }
                  required
                />

                <div className={styles.fileBlock}>
                  <input
                    className={styles.authInput}
                    type="file"
                    accept="application/pdf,image/jpeg,image/jpg,image/png"
                    onChange={(e) =>
                      setVerifyForm((p) => ({
                        ...p,
                        supporting_doc: e.target.files?.[0] || null,
                      }))
                    }
                    required
                  />

                  <p>
                    Upload <strong>one file</strong>: PDF, JPG, JPEG or PNG.
                  </p>
                </div>

                {verifyErr && (
                  <div className={styles.authError}>{verifyErr}</div>
                )}
                {verifyMsg && (
                  <div className={styles.authSuccess}>{verifyMsg}</div>
                )}

                <div className={styles.profileActions}>
                  <button
                    type="button"
                    className={styles.inlineBtn}
                    onClick={closeVerifyModal}
                    disabled={verifySubmitting}
                  >
                    Cancel
                  </button>

                  <button
                    className={styles.primaryBtn}
                    type="submit"
                    disabled={verifySubmitting}
                  >
                    {verifySubmitting ? "Saving..." : "Save & activate"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
